import { useState, useCallback, useEffect } from 'react'
import { PageHeader, DataTable, ColumnSettingsButton, btnPrimary, btnDanger, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { ExternalApiKeyModal, PlaintextRevealModal, type ExternalApiKeyRow } from './ExternalApiKeyModal'
import { useTableColumns } from '../../hooks/use-table-columns'

function formatDate(dt: string | null): string {
  if (!dt) return '—'
  try {
    return new Date(dt).toLocaleDateString()
  } catch {
    return dt
  }
}

function StatusPill({ revoked }: { revoked: boolean }) {
  const bg = revoked ? '#fca5a5' : '#bbf7d0'
  const color = revoked ? '#7f1d1d' : '#14532d'
  const label = revoked ? 'Revoked' : 'Active'
  return (
    <span
      style={{
        display: 'inline-block',
        background: bg,
        color,
        borderRadius: '9999px',
        padding: '2px 10px',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  )
}

function ScopeBadge({ row }: { row: ExternalApiKeyRow }) {
  const label =
    row.scope === 'org_scoped'
      ? `org: ${(row.orgHmac ?? '').slice(0, 8)}…`
      : row.scope === 'view_scoped'
        ? `views: ${(row.allowedViews ?? []).join(',')}`
        : 'all_orgs'
  return (
    <code
      style={{
        background: '#eff6ff',
        color: '#1d4ed8',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
      }}
    >
      {label}
    </code>
  )
}

export function ExternalApiKeysPage() {
  const [keys, setKeys] = useState<readonly ExternalApiKeyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [plaintextInfo, setPlaintextInfo] = useState<{ plaintext: string; keyName: string } | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const { tableId: _, ...tableColumnProps } = useTableColumns('external-api-keys')

  useEffect(() => {
    let cancelled = false
    // includeRevoked so the admin sees an audit trail of every key ever
    // issued, not just the live set. Matches AgentTokensPage behavior.
    api
      .get<readonly ExternalApiKeyRow[]>('/admin/external-api-keys?includeRevoked=true')
      .then((res) => {
        if (cancelled) return
        setKeys(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load keys')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  const handleRevoke = useCallback((id: string) => {
    setRevoking(id)
    api
      .post<void>(`/admin/external-api-keys/${id}/revoke`, {})
      .then(() => {
        setRevoking(null)
        setRefreshKey((k) => k + 1)
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Failed to revoke key')
        setRevoking(null)
      })
  }, [])

  const handleCreated = useCallback((plaintext: string, keyName: string) => {
    setShowCreate(false)
    setPlaintextInfo({ plaintext, keyName })
    setRefreshKey((k) => k + 1)
  }, [])

  const columns: readonly Column<ExternalApiKeyRow>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (r) => <strong style={{ fontSize: '0.875rem' }}>{r.name}</strong>,
    },
    {
      key: 'scope',
      header: 'Scope',
      render: (r) => <ScopeBadge row={r} />,
    },
    {
      key: 'rateLimitPerMinute',
      header: 'Rate',
      sortable: true,
      render: (r) => `${r.rateLimitPerMinute}/min`,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (r) => formatDate(r.createdAt),
    },
    {
      key: 'lastUsedAt',
      header: 'Last Used',
      render: (r) => formatDate(r.lastUsedAt),
    },
    {
      key: 'revokedAt',
      header: 'Status',
      render: (r) => <StatusPill revoked={!!r.revokedAt} />,
    },
    {
      key: 'id',
      header: '',
      render: (r) => {
        const canRevoke = !r.revokedAt
        return (
          <button
            style={{ ...btnDanger, fontSize: '0.75rem', padding: '3px 10px', opacity: canRevoke ? 1 : 0.4 }}
            disabled={!canRevoke || revoking === r.id}
            onClick={(e) => {
              e.stopPropagation()
              handleRevoke(r.id)
            }}
          >
            {revoking === r.id ? 'Revoking…' : 'Revoke'}
          </button>
        )
      },
    },
  ]

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader
        title="External API Keys"
        subtitle="Issue X-API-Key credentials for cross-org analytics consumers (billing, reporting, research)"
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button style={btnPrimary} onClick={() => setShowCreate(true)}>
              Create Key
            </button>
          </>
        }
      />

      {error && (
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            padding: '0.625rem 0.875rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            color: '#991b1b',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>Loading…</div>
      ) : (
        <DataTable
          columns={columns}
          rows={keys as ExternalApiKeyRow[]}
          keyField="id"
          emptyMessage="No external API keys yet"
          {...tableColumnProps}
        />
      )}

      {showCreate && <ExternalApiKeyModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}

      {plaintextInfo && (
        <PlaintextRevealModal
          plaintext={plaintextInfo.plaintext}
          keyName={plaintextInfo.keyName}
          onClose={() => setPlaintextInfo(null)}
        />
      )}
    </div>
  )
}
